import { getNotificationsUrl } from './api/notificationsClient';

// Every verified call carries the hub session: code_id AND the secret /verify/check minted (query for GETs, body for POSTs).
// null = the check gave none; the server decides.
const sessionField = (s: string | null) => (s ? { session: s } : {});
export const sessionQuery = (s: string | null) => (s ? `&session=${encodeURIComponent(s)}` : '');

export class HubApiError extends Error {
  constructor(public code: string, message: string) { super(message); }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
async function readJson(res: Response): Promise<any> {
  const d = await res.json().catch(() => ({}));
  // A 401 always means the hub session is gone; callers key on 'http_401' to drop it.
  if (res.status === 401) throw new HubApiError('http_401', d.error ?? 'Your verification expired.');
  if (!res.ok || d.success === false) throw new HubApiError(d.code ?? `http_${res.status}`, d.error ?? 'Something went wrong. Please try again.');
  return d;
}

export async function updateHubHours(token: string, codeId: number, session: string | null, hours: number): Promise<number> {
  const res = await fetch(`${getNotificationsUrl()}/v1/qr-tokens/hours`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ token, code_id: codeId, ...sessionField(session), hours }),
  });
  const d = await readJson(res);
  return d.hours;
}

/** Multipart photo upload; returns the storage path to send as image_path. 401 on a stale code. */
export async function uploadHubImage(token: string, codeId: number, image: Blob, filename: string): Promise<string> {
  const form = new FormData();
  form.append('token', token); form.append('code_id', String(codeId)); form.append('file', image, filename);
  const d = await readJson(await fetch(`${getNotificationsUrl()}/v1/qr-tokens/upload-image`, { method: 'POST', body: form }));
  return d.path as string;
}

export async function submitPartsQuote(token: string, body: { code_id?: number; session?: string; description: string; quantity?: number; image_path?: string; name?: string; phone?: string; email?: string }): Promise<number> {
  const d = await readJson(await fetch(`${getNotificationsUrl()}/v1/qr-tokens/parts-quote`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ token, ...body }) }));
  return d.lead_id as number;
}

export interface HubPartIdentification { part_number: string | null; description: string | null; confidence: 'high' | 'medium' | 'low' }
/** Verified-only "Describe it" search: the customer's words plus the sticker's make and model -> an OEM number (or null) from the AI service. Takes seconds. */
export async function identifyHubPart(token: string, codeId: number, session: string | null, description: string): Promise<HubPartIdentification> {
  const d = await readJson(await fetch(`${getNotificationsUrl()}/v1/qr-tokens/identify-part`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ token, code_id: codeId, ...sessionField(session), description }),
  }));
  return { part_number: d.part_number ?? null, description: d.description ?? null, confidence: d.confidence ?? 'low' };
}

export async function partsByMachine(organizationId: string, machineId: number): Promise<{ parts: { part_number: string; description: string | null; image_url: string | null; list_price: number | null }[]; total_matched: number; truncated: boolean }> {
  const { supabase } = await import('../config/supabaseClient');
  const { data, error } = await supabase.functions.invoke('parts-widget', { body: { organizationId, action: 'byMachine', machineId, limit: 100 } });
  if (error) throw new HubApiError('request_failed', error.message);
  if (data?.code) throw new HubApiError(data.code, data.error ?? 'Lookup failed');
  return data;
}

export type HubActionPurpose = 'quote_approval' | 'scope_approval' | 'repair_confirmation' | 'status_confirmation';
export interface HubStatusAction { purpose: HubActionPurpose; url: string }
export interface HubRequestStatus {
  id: number;
  status: { name: string | null; color: string | null; lifecycle_phase: string | null };
  created_at: string; scheduled_date: string | null; work_completed_at: string | null;
  status_reason: string | null; source: string | null; technician_name: string | null;
  action: HubStatusAction | null;
}
export interface HubAgreement {
  agreement_number: string | null; status: string; years: number;
  next_window: { window_start: string; window_end: string; status: string } | null;
}
export interface HubStatusPayload { requests: HubRequestStatus[]; agreement: HubAgreement | null }

/** Verified-only. 401 (code http_401) once the code's hour is up. */
export async function fetchHubStatus(token: string, codeId: number, session: string | null): Promise<HubStatusPayload> {
  const d = await readJson(await fetch(`${getNotificationsUrl()}/v1/qr-tokens/status?token=${encodeURIComponent(token)}&code_id=${codeId}${sessionQuery(session)}`));
  return { requests: d.requests ?? [], agreement: d.agreement ?? null };
}

export interface ValuationBody { engine_hours: number; condition_rating: 'nice' | 'poor'; is_not_running: boolean; is_damaged: boolean; notes?: string }
/** Verified-only. The customer never sees the number: the estimate goes to the dealership's Sales list. */
export async function submitValuation(token: string, codeId: number, session: string | null, body: ValuationBody): Promise<number> {
  const d = await readJson(await fetch(`${getNotificationsUrl()}/v1/qr-tokens/valuation`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ token, code_id: codeId, ...sessionField(session), ...body }),
  }));
  return d.lead_id as number;
}
